import React, { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import ThreeChartWaterDisplay from "./charts/CombinedWaterByStages";
import CombinedCropCoefficient from "./charts/CombinedCropCoefficient";

const PUBLIC_BASE_PATH = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const API_URL = (
  process.env.REACT_APP_API_URL || `${PUBLIC_BASE_PATH}/backend`
).replace(/\/$/, "");

const DEFAULT_CROP_TYPES = [
  "wheat",
  "rice",
  "maize",
  "cotton",
  "potato",
  "tomato",
  "onion",
  "sugarcane",
];

const FormField = ({ label, error, children, required = false }) => (
  <div className="form-field">
    <label className="form-label">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {error && <span className="form-error">{error}</span>}
  </div>
);

const Input = React.forwardRef(({ error, className = "", ...props }, ref) => (
  <input
    ref={ref}
    className={`input-modern ${error ? "input-error" : ""} ${className}`}
    {...props}
  />
));

const Select = React.forwardRef(
  ({ error, children, className = "", ...props }, ref) => (
    <select
      ref={ref}
      className={`input-modern select-modern ${error ? "input-error" : ""} ${className}`}
      {...props}
    >
      {children}
    </select>
  )
);

const Button = ({
  variant = "primary",
  size = "md",
  children,
  className = "",
  ...props
}) => {
  const baseClass = "btn-modern";
  const variantClass = `btn-${variant}`;
  const sizeClass = size !== "md" ? `btn-${size}` : "";

  return (
    <button
      className={`${baseClass} ${variantClass} ${sizeClass} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const Card = ({ children, className = "" }) => (
  <div className={`card ${className}`}>{children}</div>
);

const Badge = ({ children, variant = "primary" }) => (
  <span className={`badge badge-${variant}`}>{children}</span>
);

const Alert = ({ type, children, onClose }) => (
  <div className={`alert alert-${type}`}>
    <div className="alert-content">
      <span className="alert-icon">
        {type === "error" ? "⚠️" : type === "success" ? "✅" : "ℹ️"}
      </span>
      <span>{children}</span>
    </div>
    {onClose && (
      <button onClick={onClose} className="alert-close">
        ×
      </button>
    )}
  </div>
);

const MultiCropFormComponent = ({
  latitude,
  longitude,
  area = 1.0,
  crop_type,
  planting_date,
  onCropInfoChange,
  onDataReceived,
  mapActions,

  detectedProvince,
  selectedProvince,

  showLocationInputs = true,
  showChartsSection = true,
  maxCrops = null,
  initialCrops = [],
  readOnly = false,
  customStyles = {},
}) => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [combinedData, setCombinedData] = useState(null);
  const [availableCrops, setAvailableCrops] = useState(DEFAULT_CROP_TYPES);
  const [formKey, setFormKey] = useState(Date.now());

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      latitude: latitude ? parseFloat(latitude).toFixed(2) : "33.68",
      longitude: longitude ? parseFloat(longitude).toFixed(2) : "73.05",
      mainCrop: {
        crop_type: crop_type || "wheat",
        area: area || 1.0,
        planting_date: planting_date || new Date().toISOString().split("T")[0],
      },
      additionalCrops: initialCrops || [],
    },
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "additionalCrops",
  });

  const watchedValues = watch();
  const selectedCsvFile = "ssp.csv";
  const effectiveMaxCrops = maxCrops || availableCrops.length;
  const canAddMoreCrops = fields.length < effectiveMaxCrops - 1;

  const getEffectiveProvince = () => {
    return selectedProvince || detectedProvince || null;
  };

  const fetchCropsFromCSV = async () => {
    try {
      const url = `${API_URL}/api/v1/csv/data?file_name=${selectedCsvFile}&limit=100`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch crops: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      if (data.data && Array.isArray(data.data)) {
        const crops = data.data
          .map((row) => row.crop || "")
          .filter((crop) => !!crop)
          .filter((crop, index, self) => self.indexOf(crop) === index);
        return crops;
      }

      return [];
    } catch (error) {
      console.error("Error fetching crops:", error);
      return [];
    }
  };

  const getGeoTiffValue = async (lat, lng) => {
    try {
      setIsLoading(true);
      setError(null);

      const url = `${API_URL}/api/v1/geotiff/value?lat=${lat}&lng=${lng}`;
      const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API error response:", errorText);
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.in_bounds && data.value !== null) {
        return data.value;
      } else {
        setError(data.message || "No data available at this location");
        return null;
      }
    } catch (error) {
      console.error("Error fetching GeoTIFF value:", error);
      setError(`Failed to get GeoTIFF value: ${error.message}`);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getCropBandValue = async (
    lat,
    lng,
    date,
    crop,
    cropArea,
    fileName = "ET.tif"
  ) => {
    if (cropArea === undefined || cropArea === null || cropArea <= 0) {
      throw new Error("Area must be a positive number greater than 0");
    }

    try {

      const effectiveProvince = getEffectiveProvince();
      let url = `${API_URL}/api/v1/geotiff/crop-band-value?lat=${lat}&lng=${lng}&date=${date}&crop_type=${crop}&area=${cropArea}&file_name=${fileName}`;

      if (effectiveProvince) {
        url += `&province=${effectiveProvince}`;
      } else {
      }

      const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API error response:", errorText);
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Failed to retrieve data");
      }
      return data;
    } catch (error) {
      console.error("Error retrieving crop band value:", error);
      throw error;
    }
  };

  const handleAddCrop = () => {
    if (readOnly || !canAddMoreCrops) return;

    const usedCrops = [
      watchedValues.mainCrop?.crop_type,
      ...fields.map(
        (_, index) => watchedValues.additionalCrops?.[index]?.crop_type
      ),
    ].filter(Boolean);

    const availableForSelection = availableCrops.filter(
      (crop) => !usedCrops.includes(crop)
    );

    if (availableForSelection.length === 0) {
      setError("All available crops have been selected");
      return;
    }

    append({
      crop_type: availableForSelection[0],
      area: 1.0,
      planting_date: new Date().toISOString().split("T")[0],
    });

    setError(null);
    setSuccess(`Added ${availableForSelection[0]} to your crop list`);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleRemoveCrop = (index) => {
    if (readOnly) return;
    const removedCrop = watchedValues.additionalCrops?.[index];
    remove(index);
    setSuccess(`Removed ${removedCrop?.crop_type || "crop"} from your list`);
    setTimeout(() => setSuccess(null), 3000);
  };

  const getAvailableCropsForSelect = (currentCropType) => {
    const usedCrops = [
      watchedValues.mainCrop?.crop_type,
      ...fields.map(
        (_, index) => watchedValues.additionalCrops?.[index]?.crop_type
      ),
    ].filter(Boolean);

    return availableCrops.filter(
      (crop) => crop === currentCropType || !usedCrops.includes(crop)
    );
  };

  const onSubmit = async (data) => {
    if (readOnly) return;

    setIsLoading(true);
    setIsDataLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (mapActions?.updateMarkerPosition) {
        mapActions.updateMarkerPosition(
          parseFloat(data.latitude),
          parseFloat(data.longitude)
        );
      }

      const allCrops = [data.mainCrop, ...(data.additionalCrops || [])];

      const cropTypes = allCrops.map((c) => c.crop_type);
      const uniqueCropTypes = [...new Set(cropTypes)];
      if (cropTypes.length !== uniqueCropTypes.length) {
        throw new Error(
          "Duplicate crops selected. Please choose different crops."
        );
      }

      const geoTiffValue = await getGeoTiffValue(
        parseFloat(data.latitude),
        parseFloat(data.longitude)
      );

      if (geoTiffValue === null) return;

      const cropResults = [];
      for (const crop of allCrops) {
        try {
          const result = await getCropBandValue(
            parseFloat(data.latitude),
            parseFloat(data.longitude),
            crop.planting_date,
            crop.crop_type,
            crop.area
          );

          if (result) {
            cropResults.push({
              ...result,
              crop_input: crop,
              et_value:
                result.average_et_value || result.geotiff_value || geoTiffValue,
            });
          }
        } catch (cropError) {
          console.error(`Error processing ${crop.crop_type}:`, cropError);
          setError(`Error processing ${crop.crop_type}: ${cropError.message}`);
        }
      }

      if (cropResults.length > 0) {
        const combinedResult = {
          location: {
            latitude: parseFloat(data.latitude),
            longitude: parseFloat(data.longitude),
          },
          geotiff_value: geoTiffValue,
          crops: cropResults,
          timestamp: new Date().toISOString(),
          total_crops: cropResults.length,

          province_info: {
            detected_province: detectedProvince,
            selected_province: selectedProvince,
            effective_province: getEffectiveProvince(),
            data_source: selectedProvince ? "Manual Override" : "Auto-detected",
          },
        };

        setCombinedData(combinedResult);
        setFormKey(Date.now());
        setSuccess(
          `Successfully processed ${cropResults.length} crop${
            cropResults.length > 1 ? "s" : ""
          }!`
        );

        if (onDataReceived) {
          onDataReceived(combinedResult);
        }
      }
    } catch (err) {
      console.error("Error processing form submission:", err);
      setError("Failed to process request: " + err.message);
    } finally {
      setIsLoading(false);
      setIsDataLoading(false);
    }
  };

  const handleReset = () => {
    if (readOnly) return;

    reset({
      latitude: latitude ? parseFloat(latitude).toFixed(2) : "33.68",
      longitude: longitude ? parseFloat(longitude).toFixed(2) : "73.05",
      mainCrop: {
        crop_type: crop_type || "wheat",
        area: area || 1.0,
        planting_date: planting_date || new Date().toISOString().split("T")[0],
      },
      additionalCrops: initialCrops || [],
    });

    setCombinedData(null);
    setError(null);
    setSuccess(null);

    if (mapActions?.resetMap) {
      mapActions.resetMap(
        latitude ? parseFloat(latitude) : 33.6844,
        longitude ? parseFloat(longitude) : 73.0479
      );
    }

    if (onCropInfoChange) {
      onCropInfoChange(
        crop_type || "wheat",
        area || 1.0,
        planting_date || new Date().toISOString().split("T")[0]
      );
    }

    if (onDataReceived) {
      onDataReceived(null);
    }

    setSuccess("Form reset successfully");
    setTimeout(() => setSuccess(null), 3000);
  };

  useEffect(() => {
    if (latitude) setValue("latitude", parseFloat(latitude).toFixed(2));
  }, [latitude, setValue]);

  useEffect(() => {
    if (longitude) setValue("longitude", parseFloat(longitude).toFixed(2));
  }, [longitude, setValue]);

  useEffect(() => {
    const loadCrops = async () => {
      const crops = await fetchCropsFromCSV();
      if (crops.length > 0) {
        setAvailableCrops(crops);
        if (
          !crops.includes(watchedValues.mainCrop?.crop_type) &&
          crops.length > 0
        ) {
          setValue("mainCrop.crop_type", crops[0]);
        }
      } else {
        setAvailableCrops(DEFAULT_CROP_TYPES);
      }
    };
    loadCrops();
  }, [selectedCsvFile, setValue, watchedValues.mainCrop?.crop_type]);

  return (
    <>
      {}
      {(isLoading || isDataLoading) && (
        <div className="loading-overlay">
          <Card className="loading-card">
            <div className="spinner"></div>
            <h3 className="loading-title">
              {isDataLoading
                ? "Processing Crop Data..."
                : "Loading ET Value..."}
            </h3>
            <p className="loading-text">
              Please wait while we analyze your crops
            </p>
          </Card>
        </div>
      )}

      <div className="container">
        {}
        <Card>
          {}
          {error && (
            <Alert type="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert type="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {}
            <div className="section">
              <div className="section-header">
                <h3 className="section-title">
                  <span className="crop-number">1</span>
                  Primary Crop
                </h3>
              </div>

              {}
              {showLocationInputs && (
                <div className="location-grid">
                  <Controller
                    name="latitude"
                    control={control}
                    rules={{
                      required: "Latitude is required",
                      min: { value: -90, message: "Invalid latitude" },
                      max: { value: 90, message: "Invalid latitude" },
                    }}
                    render={({ field }) => (
                      <FormField
                        label="Latitude"
                        error={errors.latitude?.message}
                        required
                      >
                        <Input
                          type="number"
                          step="0.01"
                          disabled={readOnly}
                          error={errors.latitude}
                          {...field}
                        />
                      </FormField>
                    )}
                  />

                  <Controller
                    name="longitude"
                    control={control}
                    rules={{
                      required: "Longitude is required",
                      min: { value: -180, message: "Invalid longitude" },
                      max: { value: 180, message: "Invalid longitude" },
                    }}
                    render={({ field }) => (
                      <FormField
                        label="Longitude"
                        error={errors.longitude?.message}
                        required
                      >
                        <Input
                          type="number"
                          step="0.01"
                          disabled={readOnly}
                          error={errors.longitude}
                          {...field}
                        />
                      </FormField>
                    )}
                  />
                </div>
              )}

              {}
              <div className="form-grid">
                <Controller
                  name="mainCrop.crop_type"
                  control={control}
                  rules={{ required: "Crop type is required" }}
                  render={({ field }) => (
                    <FormField
                      label="Crop Type"
                      error={errors.mainCrop?.crop_type?.message}
                      required
                    >
                      <Select
                        disabled={readOnly}
                        error={errors.mainCrop?.crop_type}
                        {...field}
                      >
                        {getAvailableCropsForSelect(field.value).map(
                          (cropOption) => (
                            <option key={cropOption} value={cropOption}>
                              {cropOption.charAt(0).toUpperCase() +
                                cropOption.slice(1)}
                            </option>
                          )
                        )}
                      </Select>
                    </FormField>
                  )}
                />

                <Controller
                  name="mainCrop.area"
                  control={control}
                  rules={{
                    required: "Area is required",
                    min: { value: 0.1, message: "Area must be at least 0.1" },
                  }}
                  render={({ field }) => (
                    <FormField
                      label="Area (acres)"
                      error={errors.mainCrop?.area?.message}
                      required
                    >
                      <Input
                        type="number"
                        min="0.1"
                        step="0.1"
                        disabled={readOnly}
                        error={errors.mainCrop?.area}
                        {...field}
                      />
                    </FormField>
                  )}
                />

                <Controller
                  name="mainCrop.planting_date"
                  control={control}
                  rules={{ required: "Planting date is required" }}
                  render={({ field }) => (
                    <FormField
                      label="Planting Date"
                      error={errors.mainCrop?.planting_date?.message}
                      required
                    >
                      <Input
                        type="date"
                        disabled={readOnly}
                        error={errors.mainCrop?.planting_date}
                        {...field}
                      />
                    </FormField>
                  )}
                />
              </div>
            </div>

            {}
            <div className="section">
              <div className="section-header">
                <h3 className="section-title">
                  Additional Crops
                  <Badge variant="primary">
                    {fields.length} / {maxCrops || availableCrops.length - 1}
                  </Badge>
                </h3>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="success"
                    onClick={handleAddCrop}
                    disabled={!canAddMoreCrops || isLoading || isDataLoading}
                  >
                    <span>+</span>
                    Add Crop
                  </Button>
                )}
              </div>

              {fields.map((field, index) => (
                <Card key={field.id} className="crop-card">
                  <div className="crop-card-header">
                    <div className="crop-card-title">
                      <span className="crop-number">{index + 2}</span>
                      <h3>Additional Crop {index + 1}</h3>
                    </div>
                    {!readOnly && (
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemoveCrop(index)}
                        disabled={isLoading || isDataLoading}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <div className="form-grid">
                    <Controller
                      name={`additionalCrops.${index}.crop_type`}
                      control={control}
                      rules={{ required: "Crop type is required" }}
                      render={({ field: controllerField }) => (
                        <FormField
                          label="Crop Type"
                          error={
                            errors.additionalCrops?.[index]?.crop_type?.message
                          }
                          required
                        >
                          <Select
                            disabled={readOnly || isLoading || isDataLoading}
                            error={errors.additionalCrops?.[index]?.crop_type}
                            {...controllerField}
                          >
                            {getAvailableCropsForSelect(
                              controllerField.value
                            ).map((cropOption) => (
                              <option key={cropOption} value={cropOption}>
                                {cropOption.charAt(0).toUpperCase() +
                                  cropOption.slice(1)}
                              </option>
                            ))}
                          </Select>
                        </FormField>
                      )}
                    />

                    <Controller
                      name={`additionalCrops.${index}.area`}
                      control={control}
                      rules={{
                        required: "Area is required",
                        min: {
                          value: 0.1,
                          message: "Area must be at least 0.1",
                        },
                      }}
                      render={({ field: controllerField }) => (
                        <FormField
                          label="Area (acres)"
                          error={errors.additionalCrops?.[index]?.area?.message}
                          required
                        >
                          <Input
                            type="number"
                            min="0.1"
                            step="0.1"
                            disabled={readOnly || isLoading || isDataLoading}
                            error={errors.additionalCrops?.[index]?.area}
                            {...controllerField}
                          />
                        </FormField>
                      )}
                    />

                    <Controller
                      name={`additionalCrops.${index}.planting_date`}
                      control={control}
                      rules={{ required: "Planting date is required" }}
                      render={({ field: controllerField }) => (
                        <FormField
                          label="Planting Date"
                          error={
                            errors.additionalCrops?.[index]?.planting_date
                              ?.message
                          }
                          required
                        >
                          <Input
                            type="date"
                            disabled={readOnly || isLoading || isDataLoading}
                            error={
                              errors.additionalCrops?.[index]?.planting_date
                            }
                            {...controllerField}
                          />
                        </FormField>
                      )}
                    />
                  </div>
                </Card>
              ))}

              {fields.length === 0 && (
                <div className="empty-state">
                  <div className="empty-icon">🌱</div>
                  <h3>No additional crops yet</h3>
                  <p>Click "Add Crop" to include more crops in your analysis</p>
                </div>
              )}
            </div>

            {}
            {!readOnly && (
              <div className="button-group">
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={isLoading || isDataLoading || !isValid}
                >
                  {isLoading || isDataLoading ? (
                    <>
                      <div className="button-spinner"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <span>🚀</span>
                      Analyze {1 + fields.length} Crop
                      {fields.length !== 0 ? "s" : ""}
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleReset}
                  disabled={isLoading || isDataLoading}
                >
                  🔄 Reset
                </Button>
              </div>
            )}
          </form>
        </Card>

        {}
        {showChartsSection && combinedData?.crops?.length > 0 && (
          <Card className="chart-container">
            <div className="section-header">
              <h3 className="section-title">
                📊 Analysis Results
                <Badge variant="primary">
                  {combinedData.crops.length} Crop
                  {combinedData.crops.length > 1 ? "s" : ""} Analyzed
                </Badge>
              </h3>
            </div>

            {}
            <div className="summary-grid">
              <div className="summary-card location">
                <div className="summary-icon">🌍</div>
                <div className="summary-label">Location</div>
                <div className="summary-value">
                  {combinedData.location.latitude}°,{" "}
                  {combinedData.location.longitude}°
                </div>
              </div>

              <div className="summary-card crops">
                <div className="summary-icon">🌾</div>
                <div className="summary-label">Total Crops</div>
                <div className="summary-value">{combinedData.total_crops}</div>
              </div>

              <div className="summary-card area">
                <div className="summary-icon">💧</div>
                <div className="summary-label">Total Area</div>
                <div className="summary-value">
                  {combinedData.crops
                    .reduce((sum, crop) => sum + (parseFloat(crop.crop_input.area) || 0), 0)
                    .toFixed(1)}{" "}
                  acres
                </div>
              </div>

              <div className="summary-card date">
                <div className="summary-icon">📅</div>
                <div className="summary-label">Analysis Date</div>
                <div className="summary-value">
                  {new Date(combinedData.timestamp).toLocaleDateString()}
                </div>
              </div>
            </div>

            {}
            <ThreeChartWaterDisplay
              key={`three-charts-${formKey}`}
              combinedData={combinedData}
            />
            <div style={{ marginTop: "24px" }}>
              <CombinedCropCoefficient combinedData={combinedData} />
            </div>
          </Card>
        )}

        {}
        <style jsx>{`
          .container {
            margin: 0 auto;
            font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
              sans-serif;
          }

          .header {
            text-align: center;
            margin-bottom: 40px;
          }

          .title {
            font-size: 2.5rem;
            font-weight: 700;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 8px;
          }

          .subtitle {
            font-size: 1.1rem;
            color: #64748b;
            font-weight: 400;
          }

          .card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 24px;
            padding: 10px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1),
              0 8px 32px rgba(0, 0, 0, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .section {
            margin-bottom: 5px;
          }

          .section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 5px;
            border-bottom: 2px solid #e2e8f0;
            flex-wrap: wrap;
            gap: 12px;
          }

          .section-title {
            font-weight: 600;
            color: #1e293b;
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .crop-number {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.9rem;
            font-weight: 600;
          }

          .location-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-bottom: 10px;
          }

          .form-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1 0px;
          }

          .form-field {
            display: flex;
            flex-direction: column;
            margin-bottom: var(--space-4);
          }

          .form-label {
            display: block;
            font-size: var(--font-size-sm);
            font-weight: var(--font-weight-medium);
            color: var(--color-neutral-700);
            margin-bottom: var(--space-2);
            line-height: var(--line-height-snug);
          }

          .form-input,
          .form-select {
            width: 100%;
            height: var(--input-height);
            padding: 0 var(--input-padding-x);
            font-size: var(--font-size-sm);
            font-family: var(--font-family-sans);
            border: 1px solid var(--color-neutral-300);
            border-radius: var(--radius-md);
            background: var(--color-neutral-0);
            color: var(--color-neutral-900);
            transition: all var(--transition-base);
            box-sizing: border-box;
          }

          .form-input:focus,
          .form-select:focus {
            border-color: var(--color-primary-500);
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
            outline: none;
          }

          .form-input:hover:not(:focus),
          .form-select:hover:not(:focus) {
            border-color: var(--color-neutral-400);
          }

          .form-input.error,
          .form-select.error {
            border-color: var(--color-error-500);
          }

          .form-input.error:focus,
          .form-select.error:focus {
            border-color: var(--color-error-500);
            box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
          }

          .form-select {
            appearance: none;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
            background-position: right var(--space-3) center;
            background-repeat: no-repeat;
            background-size: 1rem;
            padding-right: var(--space-10);
          }

          .form-error {
            color: var(--color-error-600);
            font-size: var(--font-size-xs);
            margin-top: var(--space-1);
            line-height: var(--line-height-snug);
          }

          .btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: var(--space-2);
            height: var(--button-height);
            padding: 0 var(--button-padding-x);
            font-family: var(--font-family-sans);
            font-size: var(--font-size-sm);
            font-weight: var(--font-weight-medium);
            line-height: 1;
            border: none;
            border-radius: var(--radius-md);
            cursor: pointer;
            transition: all var(--transition-base);
            text-decoration: none;
            white-space: nowrap;
            user-select: none;
            position: relative;
            overflow: hidden;
          }

          .btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
          }

          .btn:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: var(--shadow-md);
          }

          .btn-sm {
            height: var(--button-height-sm);
            padding: 0 var(--space-4);
            font-size: var(--font-size-xs);
          }

          .btn-lg {
            height: var(--button-height-lg);
            padding: 0 var(--space-8);
            font-size: var(--font-size-base);
          }

          .btn-primary {
            background: linear-gradient(135deg, var(--color-primary-500) 0%, var(--color-primary-600) 100%);
            color: var(--color-neutral-0);
            box-shadow: var(--shadow-sm);
          }

          .btn-primary:hover:not(:disabled) {
            background: linear-gradient(135deg, var(--color-primary-600) 0%, var(--color-primary-700) 100%);
          }

          .btn-secondary {
            background: var(--color-neutral-0);
            color: var(--color-neutral-700);
            border: 1px solid var(--color-neutral-300);
            box-shadow: var(--shadow-xs);
          }

          .btn-secondary:hover:not(:disabled) {
            background: var(--color-neutral-50);
            border-color: var(--color-neutral-400);
          }

          .btn-success {
            background: linear-gradient(135deg, var(--color-success-500) 0%, var(--color-success-600) 100%);
            color: var(--color-neutral-0);
            box-shadow: var(--shadow-sm);
          }

          .btn-danger {
            background: linear-gradient(135deg, var(--color-error-500) 0%, var(--color-error-600) 100%);
            color: var(--color-neutral-0);
            box-shadow: var(--shadow-sm);
          }

          .badge {
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 0.85rem;
            font-weight: 500;
          }

          .badge-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }

          .alert {
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .alert-content {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 0.95rem;
          }

          .alert-close {
            background: none;
            border: none;
            font-size: 1.2rem;
            cursor: pointer;
            padding: 0;
            margin-left: 12px;
          }

          .alert-error {
            background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
            border: 1px solid #fecaca;
            color: #dc2626;
          }

          .alert-success {
            background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
            border: 1px solid #bbf7d0;
            color: #16a34a;
          }

          .crop-card {
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            border: 2px solid #e2e8f0;
            border-radius: 16px;
            padding: 10px;
          }

          .crop-card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;
          }

          .crop-card-title {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .crop-card-title h3 {
            margin: 0;
            color: #1e293b;
            font-size: 1.1rem;
          }

          .empty-state {
            text-align: center;
            padding: 2px 5px;
            color: #64748b;
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            border-radius: 16px;
            border: 2px dashed #e2e8f0;
          }

          .empty-icon {
            font-size: 2rem;
            margin-bottom: 5px;
          }

          .empty-state h3 {
            color: #475569;
          }

          .empty-state p {
            margin: 0;
            font-size: 0.95rem;
          }

          .button-group {
            display: flex;
            gap: 16px;
            justify-content: center;
            flex-wrap: wrap;
          }

          .loading-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .loading-card {
            background: white;
            padding: 32px 48px;
            border-radius: 16px;
            text-align: center;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
          }

          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #e5e7eb;
            border-top: 4px solid #667eea;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 16px;
          }

          .button-spinner {
            width: 16px;
            height: 16px;
            border: 2px solid transparent;
            border-top: 2px solid currentColor;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          .loading-title {
            margin: 0 0 8px;
            color: #1e293b;
          }

          .loading-text {
            margin: 0;
            color: #64748b;
          }

          .chart-container {
            margin-top: 32px;
          }

          .summary-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 16px;
            margin-bottom: 32px;
          }

          .summary-card {
            padding: 20px;
            border-radius: 16px;
            text-align: center;
            color: white;
          }

          .summary-card.location {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }

          .summary-card.crops {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          }

          .summary-card.area {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          }

          .summary-card.date {
            background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          }

          .summary-icon {
            font-size: 2rem;
            margin-bottom: 8px;
          }

          .summary-label {
            font-size: 0.9rem;
            opacity: 0.9;
          }

          .summary-value {
            font-size: 1.1rem;
            font-weight: 600;
          }

          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }

          @media (max-width: 768px) {
            .location-grid,
            .form-grid {
              grid-template-columns: 1fr;
            }

            .section-header {
              flex-direction: column;
              align-items: flex-start;
            }

            .button-group {
              flex-direction: column;
            }

            .crop-card-header {
              flex-direction: column;
              align-items: flex-start;
              gap: 12px;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default MultiCropFormComponent;
